import { Box, Typography, Stack, Grid, Avatar } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PetsIcon from '@mui/icons-material/Pets';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AddIcon from '@mui/icons-material/Add';
import { Badge } from '../../../../shared/components/ui/Badge';
import { Card } from '../../../../shared/components/ui/Card';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { User } from '../../context/UserContext';
import { getAttributeValue } from '../../../../utils/getAttributeLabelAndValue';
import { Button } from '../../../../shared/components/ui/Button';
import { Modal } from '../../../../shared/components/ui/Modal';
import { useState } from 'react';
import PetForm from '../Dialog/Pet/Form';
import VehicleForm from '../Dialog/Vehicle/Form';
import { PetList } from '../Dialog/Pet/List';
import { VehicleList } from '../Dialog/Vehicle/List';

interface UserInfoProps {
  user: User;
}


const formatDate = (dateString: string): string => {
  try {
    return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return 'Data inválida';
  }
};

export function UserInfo({ user }: UserInfoProps) {
  console.log('UserInfo', user);
  
  const [openPetModal, setOpenPetModal] = useState(false);
  const [openVehicleModal, setOpenVehicleModal] = useState(false);

  return (
    <>
      {/* Avatar e Informações Básicas */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 4 }}>
        <Box sx={{ position: 'relative', width: 110, height: 110 }}>
          <Avatar 
            src={user.imageUrl}
            sx={{ width: 110, height: 110, fontSize: 48, bgcolor: 'primary.main' }}
          >
            {user.name?.[0]?.toUpperCase()}
          </Avatar>
        </Box>
        <Box flex={1}>
          <Typography sx={{ color: '#23272E', fontWeight: 800, fontSize: 28, letterSpacing: -1 }}>
            {user.name} <Badge color={user.status == 'active' ? 'success' : 'default'}>{user.status}</Badge>
          </Typography>
          <Typography sx={{ color: '#6B7280', fontSize: 16, fontWeight: 500 }}>
            {user.role}
          </Typography>
          <Stack direction="row" spacing={2} mt={1} alignItems="center">
            {user.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ color: '#6B7280', fontSize: 16 }} />
                <Typography sx={{ color: '#6B7280', fontSize: 14 }}>{user.email}</Typography>
              </Box>
            )}
            {user.phoneNumber && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ color: '#6B7280', fontSize: 16 }} />
                <Typography sx={{ color: '#6B7280', fontSize: 14 }}>{user.phoneNumber}</Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Cards de Veículos e Pets */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DirectionsCarIcon sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color="#23272E">Veículos Registrados</Typography>
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" size="small" sx={{ minWidth: 32, width: 32, height: 32, borderRadius: '50%', p: 0 }} onClick={() => setOpenVehicleModal(true)}>
                <AddIcon />
              </Button>
            </Box>
            <VehicleList userId={user.id} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PetsIcon sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
                <Typography fontWeight={700} fontSize={17} color="#23272E">Pets</Typography>
              </Box>
              <Button variant="contained" size="small" sx={{ minWidth: 32, width: 32, height: 32, borderRadius: '50%', p: 0 }}  onClick={() => setOpenPetModal(true)}>
                <AddIcon />
              </Button>
            
            </Box>
        
            <Box>
              {(() => {
                const attr = user.attributes?.find(a => a.key === 'pets');
                if (!attr || !Array.isArray(attr.value) || attr.value.length === 0) {
                  return (
                      <PetList  userId={user.id}/>
                  );
                }
                return (
                  <Box>
                    {attr.value.map((pet: any, idx: number) => (
                      <Box key={pet.id || idx} sx={{ mb: 2, p: 1.5, borderRadius: 2, background: '#fff', boxShadow: '0 1px 2px #e5e7eb' }}>
                        <Typography fontWeight={700} fontSize={16}>{pet.name}</Typography>
                        <Typography fontSize={14} color="#6B7280">{pet.specie}</Typography>
                        {/* Ícones de editar/excluir podem ser adicionados aqui */}
                      </Box>
                    ))}
                  </Box>
                );
              })()}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Detalhes */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color="#23272E">Dados do Usuário</Typography>
            </Box>
            <Box component="dl" sx={{ m: 0 }}>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Nome:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.name}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>E-mail:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.email}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Telefone:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.phoneNumber}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>CPF:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.documentNumber}</dd>
              </Box>
              <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Status:</dt>
                <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>
                  <Badge color={user.status == 'active' ? 'success' : 'default'}>{user.status}</Badge>
                </dd>
              </Box>
            </Box>
          </Card>
        </Grid>
        
        {
          user.role != 'admin' && user.tenants && user.tenants.length > 0
          &&
          <Grid item xs={12} md={6}>
            <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
                <Typography fontWeight={700} fontSize={17} color="#23272E">Empresa & Perfil</Typography>
              </Box>
              <Box component="dl" sx={{ m: 0 }}>
                <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                  <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Empresa:</dt>
                  <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.tenants[0].name}</dd>
                </Box>
                <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                  <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Perfil:</dt>
                  <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{user.tenants[0].role}</dd>
                </Box>
                <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                  <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Seção:</dt>
                  <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{getAttributeValue(user.attributes ?? [], 'section_name') || '-'}</dd>
                </Box>
                <Box sx={{ display: 'flex', mb: 1, alignItems: 'center' }}>
                  <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Unidade:</dt>
                  <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>{getAttributeValue(user.attributes ?? [], 'unit') || '-'}</dd>
                </Box>
              </Box>
            </Card>
          </Grid>
        }

        {/* Card de Datas */}
        <Grid item xs={12}>
          <Card sx={{ background: '#F9FAFB', borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CalendarTodayIcon sx={{ color: '#3B4EFF', fontSize: 22, mr: 1 }} />
              <Typography fontWeight={700} fontSize={17} color="#23272E">Histórico</Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box component="dl" sx={{ m: 0 }}>
                  <Box sx={{ display: 'flex', mb: 1, alignItems: 'flex-start' }}>
                    <dt style={{ width: 120, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Criado em:</dt>
                    <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>
                      {user.createdAt ? formatDate(user.createdAt) : '-'}
                    </dd>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box component="dl" sx={{ m: 0 }}>
                  <Box sx={{ display: 'flex', mb: 1, alignItems: 'flex-start' }}>
                    <dt style={{ width: 140, color: '#6B7280', fontWeight: 500, textAlign: 'right', margin: 0, flexShrink: 0, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontSize: 15 }}>Última edição:</dt>
                    <dd style={{ margin: 0, marginLeft: 16, fontFamily: 'Inter, Roboto, Arial, sans-serif', fontWeight: 500, fontSize: 15 }}>
                      {user.updatedAt ? formatDate(user.updatedAt) : '-'}
                    </dd>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>

      {/* Modal Criar Veículo */}
      <VehicleForm 
        openVehicleModal={openVehicleModal} 
        setOpenVehicleModal={setOpenVehicleModal} 
        userId={user.id}
      />
      {/* Modal de Pet */}
      <PetForm 
        openPetModal={openPetModal} 
        setOpenPetModal={setOpenPetModal} 
        userId={user.id}
      />
    </>
  );
}